import { Group, Stack, Text, Tooltip, ActionIcon, Box } from '@mantine/core';
import Card from 'components/Card';
import React, { useState, useMemo } from 'react';
import {
  HiOutlineDocument,
  HiOutlineDocumentText,
  HiOutlinePhotograph,
  HiOutlineVideoCamera
} from 'react-icons/hi';
import { IconCheck, IconFileTypeXls, IconX } from '@tabler/icons-react';
import { MaterialData } from 'types/lcms/courses';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import ModalConfirmApproval from './ModalConfirmApproval';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { courseService } from 'services/lcms/course.service';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import DataTables from 'components/DataTables';
import Dates from 'components/Dates';
import MaterialLink from '../courses/MaterialLink';
import FormMaterialInput from './FormMaterialInput';
import MaterialInputStatus from './MaterialInputStatus';

interface MaterialInputProps {
  dataListMaterial: MaterialData[];
  hashKey: string;
  courseId: string;
  role?: 'admin' | 'sme';
  onSubmit?: (_data: {
    hash?: string;
    materials: {
      title: string;
      url: string;
      type: string;
      description?: string;
    }[];
  }) => void;
}

const MaterialInput: React.FC<MaterialInputProps> = ({
  dataListMaterial,
  hashKey,
  courseId,
  role = 'sme',
  onSubmit
}) => {
  const queryClient = useQueryClient();
  const [selectedMaterial, setSelectedMaterial] = useState({
    id: '',
    type: ''
  });
  const [openedModalConfirm, { open: openModalConfirm, close: closeModalConfirm }] =
    useDisclosure(false);

  const submitApprovalMutation = useMutation({
    mutationFn: courseService.picSubmitApproval,
    onSuccess: () => {
      showSuccessNotification({
        title: 'Success',
        message: `Material ${selectedMaterial?.type === 'approved' ? 'approved' : 'rejected'} successfully`
      });
      queryClient.invalidateQueries({
        queryKey: ['getPICDetail', hashKey]
      });
      queryClient.refetchQueries({
        queryKey: ['getPICDetail', hashKey]
      });
      queryClient.invalidateQueries({
        queryKey: ['courseDetail', courseId]
      });
      closeModalConfirm();
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Error',
        message: error?.message || `Failed to ${selectedMaterial?.type} material`
      });
    }
  });
  const { isPending } = submitApprovalMutation;

  const handleApprovalSubmit = (data: { comment: string; urlFix?: string; typeFix?: string }) => {
    const materialData = allMaterials.find((material) => material.id === selectedMaterial.id);
    if (!materialData) return;

    const decision = selectedMaterial.type === 'approved' ? 'Approved' : 'Rejected';

    submitApprovalMutation.mutate({
      hash: hashKey,
      approvals: [
        {
          materialId: materialData.materialId,
          decision,
          comments: data.comment || '',
          urlFix: data.urlFix || '',
          typeFix: data.typeFix || ''
        }
      ]
    });
  };

  const allMaterials = useMemo(() => {
    const combined = [...(dataListMaterial || [])];
    return combined.sort((a, b) => dayjs(b.submissionDate).unix() - dayjs(a.submissionDate).unix());
  }, [dataListMaterial]);

  const handleConfirm = (id: string, type: 'approved' | 'rejected') => {
    setSelectedMaterial({
      id,
      type
    });
    openModalConfirm();
  };

  const materialIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return <HiOutlineDocumentText width={16} />;
      case 'video':
        return <HiOutlineVideoCamera width={16} />;
      case 'image':
        return <HiOutlinePhotograph width={16} />;
      case 'excel':
        return <IconFileTypeXls size={16} />;
      default:
        return <HiOutlineDocument width={16} />;
    }
  };

  const columns = [
    {
      heading: 'Title',
      props: ({ title, type }: { title: string; type: string }) => (
        <Group gap={4}>
          {materialIcon(type)}
          {title}
        </Group>
      ),
      active: true,
      sortable: true
    },
    {
      heading: 'Description',
      props: 'description',
      active: true,
      sortable: true
    },
    {
      heading: 'Material',
      props: ({ url }: { url: string }) => (
        <Box maw={300} style={{ overflow: 'auto' }}>
          <MaterialLink linkMaterial={url} />
        </Box>
      ),
      active: true,
      sortable: true
    },
    {
      heading: 'Submission Date',
      props: ({ submissionDate }: { submissionDate: string }) => (
        <Dates date={submissionDate} dateFormat='dateNameTime' />
      ),
      active: true,
      sortable: true
    },
    {
      heading: 'Status',
      props: (row: MaterialData) => <MaterialInputStatus item={row as { status: string }} />,
      active: true,
      sortable: true
    },
    ...(role === 'admin'
      ? [
          {
            heading: 'Action',
            props: ({ id, status }: { id: string; status: string }) => {
              if (status !== 'Pending') return null;
              return (
                <Group gap={4} wrap='nowrap'>
                  <Tooltip label={'Approve Material'}>
                    <ActionIcon
                      variant='default'
                      c={'green'}
                      onClick={() => handleConfirm(id, 'approved')}
                    >
                      <IconCheck />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={'Reject Material'}>
                    <ActionIcon
                      variant='default'
                      c={'destructive'}
                      onClick={() => handleConfirm(id, 'rejected')}
                    >
                      <IconX />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              );
            },
            active: true
          }
        ]
      : [])
  ];

  return (
    <>
      <Card>
        <Stack gap={16}>
          <Group gap={8}>
            <HiOutlineDocumentText />
            <Text c={'dark'} fw={700}>
              Material
            </Text>
          </Group>
          <DataTables columns={columns} loading={isPending} data={dataListMaterial} withBulkData />

          <FormMaterialInput
            hashKey={hashKey}
            courseId={courseId}
            onSubmit={onSubmit || (() => {})}
            role={role}
          />
        </Stack>
      </Card>
      <ModalConfirmApproval
        materialData={allMaterials.find((material) => material.id === selectedMaterial.id) || null}
        type={selectedMaterial.type as 'approved' | 'rejected'}
        onClose={closeModalConfirm}
        opened={openedModalConfirm}
        onSubmit={handleApprovalSubmit}
      />
    </>
  );
};

export default MaterialInput;
